import type { Forfait } from './types'

/**
 * Écran « Réglages » → forfaits PME (migration 0031) : du brouillon saisi (chaînes) aux valeurs envoyées à la
 * base — logique PURE, testée. La base re-vérifie tout (contraintes de colonne) ; ceci le dit AVANT l'envoi.
 *
 * Prix vide = « sur devis » (l'abonnement porte alors son propre prix négocié) ; quota vide = illimité.
 * Modifier un forfait ne touche JAMAIS les abonnements existants : leur prix et leur quota sont figés.
 */
export interface BrouillonForfait {
  code: string
  libelle: string
  prix: string
  quota: string
  actif: boolean
}

export const brouillonForfaits = (forfaits: readonly Forfait[]): BrouillonForfait[] =>
  [...forfaits]
    .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code))
    .map((f) => ({
      code: f.code,
      libelle: f.libelle,
      prix: f.prix_mensuel_fcfa === null ? '' : String(f.prix_mensuel_fcfa),
      quota: f.quota_courses === null ? '' : String(f.quota_courses),
      actif: f.actif,
    }))

const entier = (s: string): number => {
  const t = s.replace(/[\s  ]/g, '')
  return /^\d+$/.test(t) ? Number(t) : Number.NaN
}

export type LectureForfaits =
  | { ok: true; forfaits: Pick<Forfait, 'code' | 'libelle' | 'prix_mensuel_fcfa' | 'quota_courses' | 'actif'>[] }
  | { ok: false; problemes: string[] }

export function lireBrouillonForfaits(b: readonly BrouillonForfait[]): LectureForfaits {
  const problemes: string[] = []
  const forfaits: Extract<LectureForfaits, { ok: true }>['forfaits'] = []
  for (const f of b) {
    const nom = f.libelle.trim() || f.code
    const libelle = f.libelle.trim()
    if (libelle.length < 1 || libelle.length > 80) problemes.push(`${nom} : le libellé fait entre 1 et 80 caractères.`)
    let prix: number | null = null
    if (f.prix.trim() !== '') {
      prix = entier(f.prix)
      if (Number.isNaN(prix) || prix > 100_000_000) {
        problemes.push(`${nom} : le prix est un nombre entier de francs (vide = sur devis).`)
        prix = null
      }
    }
    let quota: number | null = null
    if (f.quota.trim() !== '') {
      quota = entier(f.quota)
      if (Number.isNaN(quota) || quota < 1 || quota > 100_000) {
        problemes.push(`${nom} : le quota est un entier d'au moins 1 (vide = illimité).`)
        quota = null
      }
    }
    forfaits.push({ code: f.code, libelle, prix_mensuel_fcfa: prix, quota_courses: quota, actif: f.actif })
  }
  if (problemes.length > 0) return { ok: false, problemes }
  return { ok: true, forfaits }
}

/** Les forfaits dont le brouillon diffère de la base : on n'écrit que ceux-là. */
export function forfaitsModifies(b: readonly BrouillonForfait[], actuels: readonly Forfait[]): BrouillonForfait[] {
  const ref = new Map(brouillonForfaits(actuels).map((f) => [f.code, f]))
  return b.filter((f) => {
    const a = ref.get(f.code)
    return !a || a.libelle !== f.libelle || a.prix.replace(/\s/g, '') !== f.prix.replace(/\s/g, '') || a.quota.replace(/\s/g, '') !== f.quota.replace(/\s/g, '') || a.actif !== f.actif
  })
}
